import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, CaretLeft, CaretRight, DownloadSimple, MagicWand, MagnifyingGlass } from "@phosphor-icons/react";
import { SkeletonRows } from "@/components/RegisterSkeleton";

const inp = "w-full h-8 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function mondayOf(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hhmm = (isoStr) => { try { return new Date(isoStr).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return ""; } };

function calcHours(e) {
  if (!e.start || !e.finish) return Number(e.hours) || 0;
  const [a, b] = [e.start, e.finish].map((t) => { const [h, m] = t.split(":"); return +h + (+m || 0) / 60; });
  let d = b - a; if (d < 0) d += 24;
  return Math.max(0, +(d - (Number(e.break_minutes) || 0) / 60).toFixed(2));
}

const ST = { draft: "bg-surface-pressed text-ink-secondary", submitted: "bg-blue-50 text-blue-700", approved: "bg-emerald-50 text-emerald-700" };

function TimesheetSheet({ worker, weekStart, sheet, onClose, onSaved }) {
  const { isWriter } = useAuth();
  const blank = [...Array(7)].map((_, i) => {
    const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + i);
    return { date: iso(d), project_name: "", start: "", finish: "", break_minutes: 0, hours: 0, notes: "" };
  });
  const [entries, setEntries] = useState(() =>
    blank.map((b) => (sheet?.entries || []).find((e) => e.date === b.date) || b));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const status = sheet?.status || "draft";
  const setE = (i, k, v) => setEntries((rows) => rows.map((x, j) => j === i ? { ...x, [k]: v } : x));

  const prefill = async () => {
    setError("");
    try {
      const { data } = await api.get("/timesheets/attendance", { params: { worker_id: worker.id, week_start: weekStart } });
      setEntries((rows) => rows.map((r) => {
        const recs = data.filter((x) => x.date === r.date && x.sign_in_at);
        if (!recs.length) return r;
        const first = recs.reduce((a, b) => (a.sign_in_at < b.sign_in_at ? a : b));
        const outs = recs.filter((x) => x.sign_out_at);
        const last = outs.length ? outs.reduce((a, b) => (a.sign_out_at > b.sign_out_at ? a : b)) : null;
        return { ...r, project_name: first.project_name || r.project_name, start: hhmm(first.sign_in_at), finish: last ? hhmm(last.sign_out_at) : r.finish };
      }));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async (thenSubmit = false) => {
    setBusy(true); setError("");
    try {
      const payload = { worker_id: worker.id, week_start: weekStart, entries: entries.map((e) => ({ ...e, break_minutes: Number(e.break_minutes) || 0, hours: calcHours(e) })) };
      const { data } = await api.post("/timesheets", payload);
      if (thenSubmit) await api.post(`/timesheets/${data.id}/submit`);
      onSaved();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/timesheets/${sheet.id}/approve`); onSaved(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const total = entries.reduce((s, e) => s + calcHours(e), 0).toFixed(2);
  const locked = status === "approved";
  return (
    <div className="fixed inset-0 z-50 bg-[rgba(26,26,26,0.34)] flex justify-end eq-sheet-overlay" onClick={onClose}>
      <div className="bg-surface-panel shadow-drawer border-l border-border-warm w-full max-w-[820px] h-full flex flex-col eq-sheet" onClick={(e) => e.stopPropagation()} data-testid="timesheet-sheet">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Timesheet · Week of {weekStart}</div>
            <h3 className="font-bold text-[20px] text-ink-primary leading-tight mt-0.5">{worker.name}</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-0.5 text-[11px] font-bold rounded-[4px] ${ST[status]}`} data-testid="timesheet-status">{status.toUpperCase()}</span>
            <button onClick={onClose} data-testid="timesheet-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-3">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="timesheet-error">{error}</div>}
          {!locked && (
            <button onClick={prefill} data-testid="timesheet-prefill" className="h-8 px-3 border border-border-control bg-white text-xs font-bold rounded-[7px] hover:bg-surface-panel inline-flex items-center gap-1.5">
              <MagicWand size={13} weight="bold" /> Prefill from sign-ins
            </button>
          )}
          <table className="w-full text-cell">
            <thead><tr className="bg-surface-sunken text-left text-colhead uppercase tracking-[0.1em] text-ink-muted">
              {["Day", "Project / Site", "Start", "Finish", "Break (min)", "Hours", "Notes"].map((h) => <th key={h} className="px-2 py-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e.date} className="border-b border-divider-row" data-testid={`timesheet-day-${i}`}>
                  <td className="px-2 py-1 whitespace-nowrap font-semibold text-ink-primary">{DAYS[i]} <span className="text-ink-tertiary font-normal">{e.date.slice(8)}/{e.date.slice(5, 7)}</span></td>
                  <td className="px-2 py-1"><input className={inp} value={e.project_name || ""} disabled={locked} onChange={(ev) => setE(i, "project_name", ev.target.value)} data-testid={`ts-project-${i}`} /></td>
                  <td className="px-2 py-1 w-24"><input type="time" className={inp} value={e.start || ""} disabled={locked} onChange={(ev) => setE(i, "start", ev.target.value)} data-testid={`ts-start-${i}`} /></td>
                  <td className="px-2 py-1 w-24"><input type="time" className={inp} value={e.finish || ""} disabled={locked} onChange={(ev) => setE(i, "finish", ev.target.value)} data-testid={`ts-finish-${i}`} /></td>
                  <td className="px-2 py-1 w-20"><input type="number" min="0" step="5" className={inp} value={e.break_minutes} disabled={locked} onChange={(ev) => setE(i, "break_minutes", ev.target.value)} data-testid={`ts-break-${i}`} /></td>
                  <td className="px-2 py-1 w-16 font-bold text-right text-ink-primary" data-testid={`ts-hours-${i}`}>{calcHours(e) || "—"}</td>
                  <td className="px-2 py-1"><input className={inp} value={e.notes || ""} disabled={locked} onChange={(ev) => setE(i, "notes", ev.target.value)} /></td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-accent-wash">
                <td className="px-2 py-1.5 font-bold text-ink-primary" colSpan={5}>Week total</td>
                <td className="px-2 py-1.5 font-bold text-right text-ink-primary" data-testid="timesheet-total">{total}</td><td />
              </tr>
            </tbody>
          </table>
        </div>
        {isWriter && (
          <div className="flex justify-end gap-2 px-5 py-3 bg-surface-panel border-t border-divider-inner shrink-0">
            {!locked && <>
              <button onClick={() => save(false)} disabled={busy} data-testid="timesheet-save" className="h-9 px-4 border border-border-control bg-white text-equipr-black text-[12px] uppercase tracking-[0.04em] font-semibold rounded-[7px] hover:bg-surface-panel active:bg-surface-pressed disabled:opacity-60">Save draft</button>
              <button onClick={() => save(true)} disabled={busy} data-testid="timesheet-submit" className="h-9 px-4 bg-equipr-black text-white text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] hover:bg-gray-800 active:translate-y-px disabled:opacity-60">Save &amp; Submit</button>
            </>}
            {status === "submitted" && <button onClick={approve} disabled={busy} data-testid="timesheet-approve" className="h-9 px-5 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">Approve</button>}
            {locked && <span className="text-xs text-emerald-700 self-center">Approved — locked</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Timesheets() {
  const [weekStart, setWeekStart] = useState(() => iso(mondayOf(new Date())));
  const [workers, setWorkers] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [openWorker, setOpenWorker] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, t] = await Promise.all([api.get("/operators"), api.get("/timesheets", { params: { week_start: weekStart } })]);
      setWorkers(w.data.filter((x) => x.employment_status !== "inactive"));
      setSheets(t.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const shift = (days) => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + days); setWeekStart(iso(d)); };
  const sheetFor = (wid) => sheets.find((s) => s.worker_id === wid);
  const exportCsv = () => window.open(`${api.defaults.baseURL}/timesheets/export?week_start=${weekStart}`, "_blank");

  const filtered = workers.filter((w) => !q || w.name.toLowerCase().includes(q.toLowerCase()) || (w.supplier || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="h-full flex flex-col bg-surface-page p-4">
      <div className="flex-1 min-h-0 flex flex-col bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-divider-inner shrink-0">
          <div>
            <h1 className="font-heading text-[17px] font-bold tracking-tight text-ink-primary">Timesheets</h1>
            <p className="text-[12px] text-ink-tertiary">Weekly worker hours — prefilled from site sign-ins, approved for payroll</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 mr-1">
              <button onClick={() => shift(-7)} data-testid="ts-week-prev" className="h-9 w-9 border border-border-control bg-white rounded-[7px] hover:bg-surface-panel inline-flex items-center justify-center text-ink-secondary"><CaretLeft size={14} /></button>
              <span className="text-[12px] font-bold px-2 text-ink-primary whitespace-nowrap" data-testid="ts-week-label">Week of {weekStart}</span>
              <button onClick={() => shift(7)} data-testid="ts-week-next" className="h-9 w-9 border border-border-control bg-white rounded-[7px] hover:bg-surface-panel inline-flex items-center justify-center text-ink-secondary"><CaretRight size={14} /></button>
            </div>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Worker or supplier…" data-testid="ts-search"
                className="w-56 h-[42px] pl-9 pr-3 bg-white border-[1.5px] border-ink-primary rounded-[9px] text-body focus:outline-none focus:ring-2 focus:ring-equipr-yellow/35" />
            </div>
            <button onClick={exportCsv} data-testid="ts-export-csv" className="h-[42px] px-4 bg-equipr-yellow text-equipr-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
              <DownloadSimple size={15} weight="bold" /> Export CSV
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-cell">
            <thead className="sticky top-0 z-10">
              <tr className="bg-surface-sunken text-left border-b border-border">
                {["Worker", "Supplier", "Total Hours", "Status", ""].map((h, i) => <th key={h || i} className={`px-4 py-2.5 uppercase tracking-[0.1em] text-colhead ${i === 0 ? "font-bold text-ink-secondary" : "font-medium text-ink-muted"}`}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={5} />
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-ink-tertiary">{q ? "No workers match your search." : "No workers yet."}</td></tr>
              ) : filtered.map((w) => {
                const s = sheetFor(w.id);
                return (
                  <tr key={w.id} className="border-b border-divider-row hover:bg-accent-wash cursor-pointer" data-testid={`ts-row-${w.id}`} onClick={() => setOpenWorker(w)}>
                    <td className="px-4 py-2.5 font-semibold text-ink-primary">{w.name}</td>
                    <td className="px-4 py-2.5 text-ink-tertiary">{w.supplier || "—"}</td>
                    <td className="px-4 py-2.5 font-bold text-ink-primary">{s ? s.total_hours : "—"}</td>
                    <td className="px-4 py-2.5">{s ? <span className="inline-flex items-center gap-1.5"><span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-[4px] ${ST[s.status]}`}>{s.status.toUpperCase()}</span>{s.field_submitted && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-equipr-yellow text-equipr-black rounded-[4px]" data-testid={`ts-field-tag-${w.id}`}>FIELD</span>}</span> : <span className="text-ink-tertiary text-[11px]">not started</span>}</td>
                    <td className="px-4 py-2.5 text-right text-accent-ink text-xs font-bold">Open →</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0">
          <span data-testid="ts-footer">{loading ? "Loading…" : `${filtered.length} worker${filtered.length === 1 ? "" : "s"}${q ? ` · filtered from ${workers.length}` : ""}`}</span>
        </div>
      </div>
      {openWorker && (
        <TimesheetSheet worker={openWorker} weekStart={weekStart} sheet={sheetFor(openWorker.id)}
          onClose={() => setOpenWorker(null)} onSaved={() => { setOpenWorker(null); load(); }} />
      )}
    </div>
  );
}
